<script lang="ts">
export const DRAG_DATA_TYPE = 'go/projectid'
</script>

<script setup lang="ts">
import { invariant } from '@/shared/utils/typeAssertions'
import ProjectDropdown from '@/sharedComponents/ProjectDropdown.vue'
import CircularProgress from '@/uiKit/CircularProgress.vue'
import IconSprite from '@/uiKit/IconSprite.vue'
import NavigationListItem from '@/uiKit/NavigationListItem.vue'
import type { NodeState } from '@zag-js/tree-view'
import ProjectName from '../Project/ProjectName.vue'
import ObjectURLImage from './ObjectURLImage.vue'
import { useProjects, type Project } from './useProjects'
import { useProjectTreeDragContext } from './useProjectTreeContext'
import { useRecentProjects } from './useRecentProjects'

const props = defineProps<{
  project: Project
  nodeState: NodeState
}>()

const projectsStore = useProjects()
const recentProjectsStore = useRecentProjects()
const onImageSrcError = (project: Project) => {
  projectsStore.setCoverImageDownloadError(project.id, true)
  recentProjectsStore.setCoverImageDownloadError(project.id, true)
}

const onDragStart = (e: DragEvent) => {
  invariant(e.dataTransfer, 'dataTransfer is required for dragstart events')
  e.dataTransfer.setData(DRAG_DATA_TYPE, props.project.id)
  e.dataTransfer.effectAllowed = 'move'
}

const { setDraggedOverFolderId } = useProjectTreeDragContext()
</script>

<template>
  <NavigationListItem
    class="group p-0 outline-none focus-within:bg-background-transparent-hovered hover:bg-background-transparent-hovered focus-visible:shadow-focus-ring-primary"
    draggable="true"
    :to="{
      name: 'WorkspaceProject',
      params: { projectId: project.id },
    }"
    @dragstart="onDragStart"
    @dragend="setDraggedOverFolderId(null)"
  >
    <div class="flex size-full h-7 items-center justify-between gap-2 px-1.5">
      <IconSprite
        v-if="project.coverImageDownloadError"
        class="w-4"
        icon="file"
        size="sm"
      />
      <CircularProgress
        v-else-if="project.coverImageUploading"
        size="xs"
        class="animate-spin"
      />
      <ObjectURLImage
        v-else
        class="size-4 min-w-4 rounded-full object-cover"
        :url="project.coverImageUrls.low"
        :loading="project.coverImageUploading ?? false"
        @error="onImageSrcError(project)"
      >
        <template #fallback>
          <IconSprite
            class="w-4"
            icon="file"
            size="sm"
          />
        </template>
      </ObjectURLImage>

      <p class="truncate">
        <ProjectName
          :project="project"
          :title="project.name"
        />
      </p>

      <ProjectDropdown
        class="ml-auto opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 data-[state=open]:opacity-100 focus-visible:opacity-100"
        :project="project"
        size="sm"
        :tabindex="nodeState.focused ? 0 : -1"
      />
    </div>
  </NavigationListItem>
</template>
